import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  CardActions,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  Grid,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';

// assets
import { IconBell } from '@tabler/icons-react';
import ApiCall from 'services/axiosConfig';
import { toast } from 'react-toastify';
import NotificationModal from 'ui-component/modal/NotificationModal';
import { set } from 'immutable';

// notification status options
const status = [
  {
    value: 'all',
    label: 'All Notification',
  },
  {
    value: 'new',
    label: 'New',
  },
  {
    value: 'unread',
    label: 'Unread',
  },
  {
    value: 'other',
    label: 'Other',
  },
];

// ==============================|| NOTIFICATION ||============================== //

const NotificationSection = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [notificationData, setNotificationData] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(true);
  const [notificationLoadingMore, setNotificationLoadingMore] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [openNotificationModal, setOpenNoticationModal] = useState(false);

  const limit = 5;
  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef(null);

  const GetNotificationDetails = async (page = 1, limit = 5) => {
    try {
      const getNotificationData = await ApiCall.SECURE_API.get(
        `/notification?limit=${limit}&page=${page}`
      );
      const { data } = getNotificationData;
      if (data) {
        console.log('response at notification details api call', data);
        const {
          success,
          message,
          notifications,
          totalUnreadCount,
          totalPages,
          currentPage,
        } = data;
        if (data) {
          // setNotificationData((previtem) => [...previtem, ...notifications]);
          setNotificationData(notifications);
          setNotificationCount(totalUnreadCount);
          setCurrentPage(currentPage);
          setTotalPages(totalPages);
          setNotificationLoading(false);
        } else {
          setNotificationLoading(false);
          // setNotificationLoadingMore(false);
        }
      }
    } catch (err) {
      console.error(err);
      setNotificationLoading(false);
      // setNotificationLoadingMore(false);

      if (err.code == 'ERR_NETWORK') {
        toast.error(err.message);
      } else if (err?.response?.status) {
        toast.error(err.response.data.message);
      }
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
    setNotificationData([]);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (open) {
      setNotificationLoading(true);
      GetNotificationDetails(1, 5);
    }
  }, [open]);

  const handleChange = (event) => {
    if (event?.target.value) setValue(event?.target.value);
  };

  return (
    <>
      <Box
        sx={{
          ml: 2,
          mr: 3,
          [theme.breakpoints.down('md')]: {
            mr: 2,
          },
        }}
      >
        <ButtonBase sx={{ borderRadius: '12px' }}>
          <Avatar
            variant='rounded'
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.largeAvatar,
              transition: 'all .2s ease-in-out',
              background: theme.palette.secondary.light,
              color: theme.palette.secondary.dark,
              '&[aria-controls="menu-list-grow"],&:hover': {
                background: theme.palette.secondary.dark,
                color: theme.palette.secondary.light,
              },
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup='true'
            onClick={handleToggle}
            color='inherit'
          >
            <IconBell stroke={1.5} size='1.3rem' />
          </Avatar>
        </ButtonBase>
      </Box>
      <Popper
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [matchesXs ? 5 : 0, 20],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions
            position={matchesXs ? 'top' : 'top-right'}
            in={open}
            {...TransitionProps}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  border={false}
                  elevation={16}
                  content={false}
                  boxShadow
                  shadow={theme.shadows[16]}
                >
                  <Grid container direction='column' spacing={2}>
                    <Grid item xs={12}>
                      <Grid
                        container
                        alignItems='center'
                        justifyContent='space-between'
                        sx={{ pt: 2, px: 2 }}
                      >
                        <Grid item>
                          <Stack direction='row' spacing={2}>
                            <Typography variant='subtitle1'>
                              All Notification
                            </Typography>
                            <Chip
                              size='small'
                              label={notificationCount}
                              sx={{
                                color: theme.palette.background.default,
                                bgcolor: theme.palette.warning.dark,
                              }}
                            />
                          </Stack>
                        </Grid>
                        <Grid item>
                          <Typography
                            component={Link}
                            to='#'
                            variant='subtitle2'
                            color='primary'
                            marginLeft='1rem'
                          >
                            Mark as all read
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <PerfectScrollbar
                        style={{
                          height: '100%',
                          maxHeight: 'calc(90vh - 225px)',
                          overflowX: 'hidden',
                        }}
                      >
                        <Grid container direction='column' spacing={2}>
                          {/* <Grid item xs={12}>
                            <Box sx={{ px: 2, pt: 0.25 }}>
                              <TextField
                                id='outlined-select-currency-native'
                                select
                                fullWidth
                                value={value}
                                onChange={handleChange}
                                SelectProps={{
                                  native: true,
                                }}
                              >
                                {status.map((option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </TextField>
                            </Box>
                          </Grid> */}
                          <Grid item xs={12} p={0}>
                            <Divider sx={{ my: 0 }} />
                          </Grid>
                        </Grid>
                        {notificationLoading ? (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <CircularProgress
                              color='secondary'
                              sx={{ margin: '2rem 10rem' }}
                            />
                          </div>
                        ) : (
                          <NotificationList
                            notificationData={notificationData}
                            setNotificationData={setNotificationData}
                          />
                        )}
                      </PerfectScrollbar>
                    </Grid>
                  </Grid>
                  <Divider />
                  <CardActions sx={{ p: 1.25, justifyContent: 'center' }}>
                    <Button
                      size='small'
                      disableElevation
                      onClick={() => {
                        setOpenNoticationModal(true);
                        // setNotificationLoadingMore(true);
                        // GetNotificationDetails(currentPage + 1);
                      }}
                      // disabled={notificationLoadingMore}
                    >
                      {/* {notificationLoadingMore ? 'Loading...' : 'View More'} */}
                      {'View More'}
                    </Button>
                  </CardActions>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
      <NotificationModal
        open={openNotificationModal}
        closeModal={() => setOpenNoticationModal(false)}
        currentPg={currentPage}
        totalPg={totalPages}
        totalNotificationCount={notificationCount}
        notificationData={notificationData}
        // setNotificationData={setNotificationData}
        onPageChange={(page, limit) => GetNotificationDetails(page, limit)}
      />
    </>
  );
};

export default NotificationSection;
